/**
 * Melee Swing-Cone Overlay Config
 *
 * Styling for the in-world melee hitbox visualization drawn by
 * `MeleeConeOverlayRenderer` — the melee sibling of the race hazard hit shapes
 * (`race-debug.config.ts` → renderHitboxes) and the mob aggro circles
 * (`aggro-overlay.config.ts`).
 *
 * It draws the RESOLVED cone for the swing the local player would land right
 * now: the wedge itself (origin / facing / `rangePx` / `arcDeg` from
 * `resolveMeleeCone`), plus a marker per in-cone enemy — one style for the
 * targets that take damage, another for the ones `maxTargets` cut. Every
 * number comes from `CombatManager.getMeleeConePlan`, the same call the damage
 * path uses; nothing here re-derives geometry.
 *
 * OFF by default (`defaultEnabled: false`), toggled at runtime via the
 * `toggle_melee_cone` dev action (MeleeConeOverlayState) — the 🗡 button in the
 * Player "Debug" toggle list, and `__helixDebug.action('toggle_melee_cone')`.
 *
 * Two-section file (the "defaults + dev override" pattern, see
 * aggro-overlay.config.ts). Nested style objects override wholesale. Colors are
 * numeric hex (0xRRGGBB) to match the IGraphics fill/stroke API.
 */

export interface MeleeConeWedgeStyle {
  /** Wedge fill color. */
  fillColor: number;
  /** Wedge fill alpha (a wash — the world has to stay readable underneath). */
  fillAlpha: number;
  /** Wedge outline color. */
  strokeColor: number;
  /** Wedge outline width (px). */
  strokeWidth: number;
  /** Wedge outline alpha. */
  strokeAlpha: number;
  /**
   * Arc segments used to approximate the sweep. Higher = smoother edge, more
   * vertices per frame. The polygon is inscribed at any count, so this only
   * trades smoothness for cost — it can never over-draw the hit region.
   */
  arcSegments: number;
}

export interface MeleeConeTargetStyle {
  /** Marker ring color. */
  color: number;
  /** Marker ring radius (px). */
  radius: number;
  /** Marker ring line width (px). */
  lineWidth: number;
  /** Marker ring alpha. */
  alpha: number;
}

export interface MeleeConeOverlayConfig {
  /** Whether the overlay is on at load. Debug overlay → false. */
  defaultEnabled: boolean;
  /** The swept wedge. */
  wedge: MeleeConeWedgeStyle;
  /** Enemies inside the arc that this swing damages — green. */
  hitTarget: MeleeConeTargetStyle;
  /**
   * Enemies inside the arc that `maxTargets` cut — grey. They are in the
   * shape and still take nothing, which without a marker reads as a bug.
   */
  cappedTarget: MeleeConeTargetStyle;
  /** Facing-axis reach tick drawn from origin to `rangePx`. */
  axis: { color: number; lineWidth: number; alpha: number };
  /** Origin dot — the point the cone is actually measured from. */
  originDot: { color: number; radius: number; alpha: number };
  /** Readout label above the attacker (class · ability · reach/arc/cap · counts). */
  label: { color: number; fontSize: number; offsetY: number };
}

export const MELEE_CONE_OVERLAY_DEFAULTS: MeleeConeOverlayConfig = {
  defaultEnabled: false,
  wedge: {
    fillColor: 0xff8800,
    fillAlpha: 0.16,
    strokeColor: 0xffaa33,
    strokeWidth: 2,
    strokeAlpha: 0.85,
    arcSegments: 32,
  },
  hitTarget: { color: 0x2ecc71, radius: 16, lineWidth: 3, alpha: 0.95 },
  cappedTarget: { color: 0x888888, radius: 12, lineWidth: 2, alpha: 0.7 },
  axis: { color: 0xffaa33, lineWidth: 1, alpha: 0.5 },
  originDot: { color: 0xffffff, radius: 3, alpha: 0.9 },
  label: { color: 0xffaa33, fontSize: 11, offsetY: -46 },
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through to
 * MELEE_CONE_OVERLAY_DEFAULTS. Example: `{ defaultEnabled: true }` to have the
 * wedge on from boot while tuning per-class cone profiles.
 */
export const MELEE_CONE_OVERLAY_DEV_OVERRIDE: Partial<MeleeConeOverlayConfig> = {};

export const MELEE_CONE_OVERLAY: MeleeConeOverlayConfig = {
  ...MELEE_CONE_OVERLAY_DEFAULTS,
  ...MELEE_CONE_OVERLAY_DEV_OVERRIDE,
};
